/**
 * @file: ReportLinkWidget.tsx
 *
 * Renders the "查看XXX报告 →" button a chat bot posts via a ` ```report_link `
 * fence. Tapping deep-links to the right report tab through the app-root
 * navigation ref (the same path `useOpenChatCard` uses, so it works from the
 * floating chat panel's standalone container too).
 *
 * Fence payload:
 *   `{ reportType, label, orderNo, reportNo, licensePlateNo, vehicleName, vehicleImageUrl? }`
 */

import { ReportRef } from '@euler/app/flows/report/functions';
import { getGlobalNavigationRef } from '@euler/app/navigation/RoutesContainer';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { reportContentTypeToReportTabKey } from '@euler/model/util';
import { CommonActions } from '@react-navigation/native';
import React, { memo, useCallback } from 'react';
import { Text, TouchableOpacity } from 'react-native';
import { useTheme } from 'styled-components/native';
import { ReportLinkPayload } from './payloads';

export const ReportLinkWidget = memo(
  ({ data }: { data: ReportLinkPayload }) => {
    const { rpx } = useResponsiveContext();
    const theme = useTheme();
    const label = data?.label || '查看报告';

    const onPress = useCallback(() => {
      const root = getGlobalNavigationRef();
      if (!root?.isReady()) return;
      if (!data?.reportNo) return;
      const ref: ReportRef = {
        type: 'report',
        id: data.reportNo,
        orderNo: data.orderNo ?? '',
        reportNo: data.reportNo,
        licensePlateNo: data.licensePlateNo ?? '',
        vehicleName: data.vehicleName ?? '',
        vehicleImageUrl: data.vehicleImageUrl,
      };
      root.dispatch(
        CommonActions.navigate({
          name: 'Report',
          params: {
            target: ref,
            initialTab: reportContentTypeToReportTabKey(data.reportType),
          },
        }),
      );
    }, [data]);

    return (
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.7}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          alignSelf: 'flex-start',
          backgroundColor: theme.link + '14',
          borderRadius: rpx(8),
          paddingVertical: rpx(8),
          paddingHorizontal: rpx(12),
          marginVertical: rpx(6),
        }}
      >
        <Text
          style={{ fontSize: rpx(13), color: theme.link, fontWeight: '600' }}
        >
          {label} →
        </Text>
      </TouchableOpacity>
    );
  },
);
